export interface AppSeatTier {
  readonly code: string;
  readonly label: string;
}

export interface NativeAppIdentity {
  readonly appDirName: string;
  readonly slug: string;
  readonly scheme: string;
  readonly appIdentifier: string;
}

export interface AppEntry {
  readonly key: string;
  readonly displayName: string;
  readonly authSlug: string;
  readonly productKey: string;
  readonly portalSlug: string;
  readonly telemetryTag: string;
  readonly inviteBrand: string;
  readonly portalUrlEnvVar: string;
  readonly appDirName: string;
  readonly nativeApp?: NativeAppIdentity;
  readonly routes: Record<string, (id: number) => string>;
  readonly seatTiers: readonly AppSeatTier[];
}

export const APP_REGISTRY = {
  'inbox-clinic': {
    key: 'inbox-clinic',
    displayName: 'Inbox Clinic',
    authSlug: 'clinic',
    productKey: 'inbox_clinic',
    portalSlug: 'clinic-portal',
    telemetryTag: 'inbox-clinic',
    inviteBrand: 'clinic',
    portalUrlEnvVar: 'PORTAL_URL',
    appDirName: 'apps/clinic-portal',
    routes: {
      task: (id: number) => `/tasks/${id}`,
      contact: (id: number) => `/contacts/${id}`,
    },
    seatTiers: [{ code: 'standard', label: 'Standard' }],
  },
  'skill-viper': {
    key: 'skill-viper',
    displayName: 'Skill Viper',
    authSlug: 'skill-viper',
    productKey: 'anchor_contact',
    portalSlug: 'anchor-portal',
    telemetryTag: 'skill-viper',
    inviteBrand: 'skill-viper',
    portalUrlEnvVar: 'SKILL_VIPER_PORTAL_URL',
    appDirName: 'apps/skill-viper-portal',
    routes: {
      task: (id: number) => `/tasks/${id}`,
      contact: (id: number) => `/contacts/${id}`,
    },
    seatTiers: [{ code: 'standard', label: 'Standard' }],
  },
  'job-plan': {
    key: 'job-plan',
    displayName: 'Job Plan',
    authSlug: 'job-plan',
    productKey: 'job_plan',
    portalSlug: 'job-plan-portal',
    telemetryTag: 'job-plan',
    inviteBrand: 'job-plan',
    portalUrlEnvVar: 'JOB_PLAN_PORTAL_URL',
    appDirName: 'apps/job-plan-portal',
    nativeApp: {
      appDirName: 'apps/job-plan-native-app',
      slug: 'job-plan-native-app',
      scheme: 'jobplan',
      appIdentifier: 'app.jobplan.native',
    },
    routes: {
      job: (id: number) => `/jobs/${id}`,
      task: (id: number) => `/tasks/${id}`,
    },
    seatTiers: [
      { code: 'planner', label: 'Planner' },
      { code: 'operator', label: 'Operator' },
    ],
  },
  'real-estate-studio': {
    key: 'real-estate-studio',
    displayName: 'Real Estate Studio',
    authSlug: 'real-estate-studio',
    productKey: 'real_estate_studio',
    portalSlug: 'real-estate-studio-portal',
    telemetryTag: 'real-estate-studio',
    inviteBrand: 'real-estate-studio',
    portalUrlEnvVar: 'REAL_ESTATE_STUDIO_PORTAL_URL',
    appDirName: 'apps/real-estate-studio-portal',
    nativeApp: {
      appDirName: 'apps/real-estate-studio-native-app',
      slug: 'real-estate-studio-native-app',
      scheme: 'realestatestudio',
      appIdentifier: 'au.com.realestatestudio.native',
    },
    routes: {
      property: (id: number) => `/properties/${id}`,
      studioProject: (id: number) => `/studio/${id}`,
    },
    seatTiers: [
      { code: 'agent', label: 'Agent' },
      { code: 'principal', label: 'Principal' },
    ],
  },
} as const satisfies Record<string, AppEntry>;

export type AppRegistry = typeof APP_REGISTRY;
export type AppKey = keyof AppRegistry;

export type AuthApp = AppRegistry[AppKey]['authSlug'];
export type ProductKey = AppRegistry[AppKey]['productKey'];
export type PortalApp = AppRegistry[AppKey]['portalSlug'];
/**
 * Native installations register under their own platform rather than a portal slug: a phone is
 * claimed inactive and then activated with a one-time server claim, which a browser never does.
 */
export const NATIVE_PUSH_PLATFORMS = ['clinic-native', 'real-estate-studio-native'] as const;
export type NativePushPlatform = (typeof NATIVE_PUSH_PLATFORMS)[number];

export type PushDevicePlatform = 'extension' | NativePushPlatform | PortalApp;
export type InviteBrand = AppRegistry[AppKey]['inviteBrand'];
export type SeatTierCode = AppRegistry[AppKey]['seatTiers'][number]['code'];

export const ALL_APP_KEYS = Object.keys(APP_REGISTRY) as [AppKey, ...AppKey[]];
export const ALL_PRODUCT_KEYS = Object.values(APP_REGISTRY).map((entry) => entry.productKey) as [
  ProductKey,
  ...ProductKey[],
];
export const ALL_PORTAL_SLUGS = Object.values(APP_REGISTRY).map((entry) => entry.portalSlug) as [
  PortalApp,
  ...PortalApp[],
];
export const ALL_PUSH_DEVICE_PLATFORMS = [
  'extension',
  ...NATIVE_PUSH_PLATFORMS,
  ...ALL_PORTAL_SLUGS,
] as [PushDevicePlatform, ...PushDevicePlatform[]];
/**
 * Platforms a browser or extension registers directly. Native installations are excluded: they
 * go through the claim-then-activate flow instead, so a shared phone cannot inherit push.
 */
export type RegisterablePushPlatform = Exclude<PushDevicePlatform, NativePushPlatform>;

export const ALL_REGISTERABLE_PUSH_PLATFORMS = ['extension', ...ALL_PORTAL_SLUGS] as [
  RegisterablePushPlatform,
  ...RegisterablePushPlatform[],
];

export function getAppEntry<K extends AppKey>(key: K): AppRegistry[K] {
  return APP_REGISTRY[key];
}

export function findAppByAuthSlug(authSlug: string): AppEntry | undefined {
  return Object.values(APP_REGISTRY).find((entry) => entry.authSlug === authSlug);
}

export function findAppByProductKey(productKey: string): AppEntry | undefined {
  return Object.values(APP_REGISTRY).find((entry) => entry.productKey === productKey);
}

export function findAppByPortalSlug(portalSlug: string): AppEntry | undefined {
  return Object.values(APP_REGISTRY).find((entry) => entry.portalSlug === portalSlug);
}

export function findAppByInviteBrand(inviteBrand: string): AppEntry | undefined {
  return Object.values(APP_REGISTRY).find((entry) => entry.inviteBrand === inviteBrand);
}

export function isAuthApp(value: unknown): value is AuthApp {
  return typeof value === 'string' && Object.values(APP_REGISTRY).some((e) => e.authSlug === value);
}

export function isProductKey(value: unknown): value is ProductKey {
  return (
    typeof value === 'string' && Object.values(APP_REGISTRY).some((e) => e.productKey === value)
  );
}

export function isPortalApp(value: unknown): value is PortalApp {
  return (
    typeof value === 'string' && Object.values(APP_REGISTRY).some((e) => e.portalSlug === value)
  );
}
